import React from 'react';
import { Button } from '@components';
import { Card, Form, Row, Col, ButtonGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useLocation, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Get, Put } from '@app/utils/httpProvider';
import { DOTTUYENSINH_API } from '@app/utils/apiUrl';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import * as moment from 'moment';
import { 
    messageError, 
    messageToast, 
    status200,
    loaiTuyenSinhValue,
    objectLoaiTuyenSinh,
    trangThaiDotTuyenSinhValue,
    objectTrangThaiDotTuyenSinh,
    doiTuongValue,
    objectDoiTuong 
} from '../../../../../utils/define';
import { URI_INTERNAL } from '../../../../../utils/pathLocations';
import HorizontalFieldsRow from '@app/components/common/HorizontalFieldsRow';
import DoiTuongMienGiamFields, { parseDoiTuongMienGiam, stringifyDoiTuongMienGiam } from './DoiTuongMienGiamFields';

const schema = yup.object().shape({
    tenDot: yup.string().required(messageError.emptyMessage).max(255, 'Tên đợt không được vượt quá 255 ký tự'),
    namTuyenSinh: yup.number().required(messageError.emptyMessage).min(1, 'Năm tuyển sinh phải lớn hơn 0'),
    loaiTuyenSinh: yup.string().required(messageError.emptyMessage),
    trangThaiDotTuyenSinh: yup.string().required(messageError.emptyMessage),
    doiTuong: yup.string().required(messageError.emptyMessage),
    lePhi: yup.number().min(0, 'Lệ phí không được nhỏ hơn 0').nullable(),
    chiTieuQuan: yup.number().min(0, 'Chỉ tiêu quân không được nhỏ hơn 0').nullable(),
    chiTieuDan: yup.number().min(0, 'Chỉ tiêu dân không được nhỏ hơn 0').nullable(),
    ngayNhanHoSo: yup.date().nullable(),
    ngayKetThucNhanHoSo: yup.date()
        .nullable()
        .min(yup.ref('ngayNhanHoSo'), 'Ngày kết thúc phải lớn hơn hoặc bằng ngày bắt đầu'),
    doiTuongMienGiams: yup.array().when('coDoiTuongMienGiam', {
        is: true,
        then: yup.array().of(yup.object().shape({
            tenDoiTuong: yup.string().trim().required('Vui lòng nhập tên đối tượng').max(255, 'Tên đối tượng không được vượt quá 255 ký tự')
        })).min(1, 'Vui lòng thêm ít nhất một đối tượng')
    }),
});

const DotTuyenSinhEdit = () => {
    const history = useHistory();
    const queryClient = useQueryClient();
    const location = useLocation();
    const params = new URLSearchParams(location.search.substring(1));
    const itemId = params.get('id');

    const itemQuery = useQuery(['dotTuyenSinh', itemId], () => Get(DOTTUYENSINH_API.GET_ITEM, itemId), {
        enabled: !!itemId
    });

    const editMutation = useMutation((value) => Put(DOTTUYENSINH_API.UPDATE_ITEM, itemId, value));

    const handleEdit = (values) => {
        const doiTuong = values.doiTuong;
        const { coDoiTuongMienGiam, doiTuongMienGiams, ...requestValues } = values;
        const model = {
            ...requestValues,
            chiTieuQuan: (doiTuong === doiTuongValue.quan || doiTuong === doiTuongValue.quanVaDan) ? (values.chiTieuQuan === '' ? 0 : values.chiTieuQuan) : 0,
            chiTieuDan: (doiTuong === doiTuongValue.khac || doiTuong === doiTuongValue.quanVaDan) ? (values.chiTieuDan === '' ? 0 : values.chiTieuDan) : 0,
            lePhi: values.lePhi === '' ? 0 : values.lePhi,
            ngayNhanHoSo: values.ngayNhanHoSo || null,
            ngayKetThucNhanHoSo: values.ngayKetThucNhanHoSo || null,
            doiTuongMienGiam: coDoiTuongMienGiam
                ? stringifyDoiTuongMienGiam(doiTuongMienGiams)
                : '',
        };

        editMutation.mutate(model, {
            onSuccess: (res) => {
                if (res.status !== status200) {
                    toast.error(messageToast.messageToastUpdateError);
                } else {
                    toast.success(messageToast.messageToastUpdateSuccess);
                    queryClient.setQueryData(['dotTuyenSinh', itemId], model);
                    history.push(URI_INTERNAL.DOTTUYENSINH_PATH);
                }
            },
            onError: (err) => {
                toast.error(err?.message || 'Đã có lỗi xảy ra, vui lòng thử lại !');
            }
        });
    };

    if (itemQuery.isLoading) {
        return <div>Đang tải....</div>;
    }

    if (itemQuery.isError || !itemQuery.data) {
        return <div>Có lỗi xảy ra hoặc không tìm thấy dữ liệu</div>;
    }

    const currentData = itemQuery.data;

    const initialLoai = currentData.loaiTuyenSinh;
    const initialTrangThai = currentData.trangThaiDotTuyenSinh;
    const initialDoiTuong = currentData.doiTuong;
    const initialDoiTuongMienGiam = currentData.doiTuongMienGiam ?? '';
    const normalizedDoiTuongMienGiams = parseDoiTuongMienGiam(initialDoiTuongMienGiam);

    return (
        <Card>
            <Card.Header as="h5">Sửa đợt tuyển sinh</Card.Header>
            <Card.Body>
                <Formik
                    enableReinitialize
                    validationSchema={schema}
                    onSubmit={(values) => handleEdit(values)}
                    initialValues={{
                        tenDot: currentData.tenDot ?? '',
                        namTuyenSinh: currentData.namTuyenSinh ?? new Date().getFullYear(),
                        loaiTuyenSinh: initialLoai ?? loaiTuyenSinhValue.tuyenSinhTienSi,
                        trangThaiDotTuyenSinh: initialTrangThai ?? trangThaiDotTuyenSinhValue.sapMo,
                        doiTuong: initialDoiTuong ?? doiTuongValue.quanVaDan,
                        chiTieuQuan: currentData.chiTieuQuan ?? 0,
                        chiTieuDan: currentData.chiTieuDan ?? 0,
                        lePhi: currentData.lePhi ?? 0,
                        ngayNhanHoSo: currentData.ngayNhanHoSo ? moment(currentData.ngayNhanHoSo).format('YYYY-MM-DD') : '',
                        ngayKetThucNhanHoSo: currentData.ngayKetThucNhanHoSo ? moment(currentData.ngayKetThucNhanHoSo).format('YYYY-MM-DD') : '',
                        coDoiTuongMienGiam: normalizedDoiTuongMienGiams.length > 0,
                        doiTuongMienGiams: normalizedDoiTuongMienGiams
                    }}
                >
                    {({ handleSubmit, handleChange, values, errors, setFieldValue }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Tên đợt',
                                        name: 'tenDot',
                                        component: (
                                            <>
                                                <Form.Control type="text" name="tenDot" value={values.tenDot} onChange={handleChange} />
                                                {errors.tenDot && <div className="text-danger small mt-1">{errors.tenDot}</div>}
                                            </>
                                        ),
                                        required: true
                                    },
                                    {
                                        label: 'Năm tuyển sinh',
                                        name: 'namTuyenSinh',
                                        component: (
                                            <>
                                                <Form.Control type="number" name="namTuyenSinh" value={values.namTuyenSinh} onChange={handleChange} />
                                                {errors.namTuyenSinh && <div className="text-danger small mt-1">{errors.namTuyenSinh}</div>}
                                            </>
                                        ),
                                        required: true
                                    },
                                ]}
                            />
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Loại tuyển sinh',
                                        name: 'loaiTuyenSinh',
                                        component: (
                                            <Form.Control
                                                as="select"
                                                name="loaiTuyenSinh"
                                                value={values.loaiTuyenSinh}
                                                onChange={handleChange}
                                            >
                                                {objectLoaiTuyenSinh.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </Form.Control>
                                        ),
                                        error: errors.loaiTuyenSinh
                                    },
                                    {
                                        label: 'Đối tượng',
                                        name: 'doiTuong',
                                        component: (
                                            <Form.Control
                                                as="select"
                                                name="doiTuong"
                                                value={values.doiTuong}
                                                onChange={handleChange}
                                            >
                                                {objectDoiTuong.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </Form.Control>
                                        ),
                                        error: errors.doiTuong
                                    },
                                ]}
                            />
                            <HorizontalFieldsRow
                                fields={[
                                    (parseInt(values.doiTuong) === doiTuongValue.quan || parseInt(values.doiTuong) === doiTuongValue.quanVaDan) ? {
                                        label: 'Chỉ tiêu quân',
                                        name: 'chiTieuQuan',
                                        component: (
                                            <>
                                                <Form.Control type="number" name="chiTieuQuan" value={values.chiTieuQuan} onChange={handleChange} />
                                                {errors.chiTieuQuan && <div className="text-danger small mt-1">{errors.chiTieuQuan}</div>}
                                            </>
                                        )
                                    } : null,
                                    (parseInt(values.doiTuong) === doiTuongValue.khac || parseInt(values.doiTuong) === doiTuongValue.quanVaDan) ? {
                                        label: 'Chỉ tiêu dân',
                                        name: 'chiTieuDan',
                                        component: (
                                            <>
                                                <Form.Control type="number" name="chiTieuDan" value={values.chiTieuDan} onChange={handleChange} />
                                                {errors.chiTieuDan && <div className="text-danger small mt-1">{errors.chiTieuDan}</div>}
                                            </>
                                        )
                                    } : null,
                                ]}
                            />
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Lệ phí',
                                        name: 'lePhi',
                                        component: (
                                            <>
                                                <Form.Control type="number" name="lePhi" value={values.lePhi} onChange={handleChange} />
                                                {errors.lePhi && <div className="text-danger small mt-1">{errors.lePhi}</div>}
                                            </>
                                        )
                                    },
                                    {
                                        label: 'Trạng thái',
                                        name: 'trangThaiDotTuyenSinh',
                                        component: (
                                            <Form.Control
                                                as="select"
                                                name="trangThaiDotTuyenSinh"
                                                value={values.trangThaiDotTuyenSinh}
                                                onChange={handleChange}
                                            >
                                                {objectTrangThaiDotTuyenSinh.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </Form.Control>
                                        ),
                                        error: errors.trangThaiDotTuyenSinh
                                    },
                                ]}
                            />
                            <HorizontalFieldsRow
                                fields={[
                                    {
                                        label: 'Ngày nhận hồ sơ',
                                        name: 'ngayNhanHoSo',
                                        component: (
                                            <>
                                                <Form.Control type="date" name="ngayNhanHoSo" value={values.ngayNhanHoSo} onChange={handleChange} />
                                                {errors.ngayNhanHoSo && <div className="text-danger small mt-1">{errors.ngayNhanHoSo}</div>}
                                            </>
                                        )
                                    },
                                    {
                                        label: 'Ngày kết thúc nhận',
                                        name: 'ngayKetThucNhanHoSo',
                                        component: (
                                            <>
                                                <Form.Control type="date" name="ngayKetThucNhanHoSo" value={values.ngayKetThucNhanHoSo} onChange={handleChange} />
                                                {errors.ngayKetThucNhanHoSo && <div className="text-danger small mt-1">{errors.ngayKetThucNhanHoSo}</div>}
                                            </>
                                        )
                                    },
                                ]}
                            />
                            <DoiTuongMienGiamFields values={values} errors={errors} setFieldValue={setFieldValue} />
                            <Form.Group as={Row} className="mb-3">
                                <Col md={{ span: 10, offset: 2 }}>
                                    <ButtonGroup className="mb-2">
                                        <Button type="submit" theme="primary">
                                            Cập nhật
                                        </Button>
                                        &nbsp;&nbsp;&nbsp;
                                        <Button onClick={() => history.push(URI_INTERNAL.DOTTUYENSINH_PATH)} theme="secondary">
                                            Hủy
                                        </Button>
                                    </ButtonGroup>
                                </Col>
                            </Form.Group>
                        </Form>
                    )}
                </Formik>
            </Card.Body>
        </Card>
    );
};

export default DotTuyenSinhEdit;
